import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Products = ({ cms }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const d = cms || {};
  const title = d.title || 'Featured RO Systems';
  const subtitle = d.subtitle || 'Our Products';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/products/homepage/featured`);
        if (res.ok) {
          const data = await res.json();
          setProducts(Array.isArray(data) ? data : data.products || []);
        }
      } catch (e) {
        console.error('Failed to fetch products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-700" size={32} /></div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section id="products" className="py-16 md:py-24 bg-gray-50 reveal-on-scroll" data-testid="products-section">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-xs sm:text-sm font-semibold text-blue-700 uppercase tracking-wider mb-3">{subtitle}</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
            {title.includes(' ') ? <>{title.split(' ').slice(0, -2).join(' ')} <span className="text-blue-700">{title.split(' ').slice(-2).join(' ')}</span></> : title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
          {products.map((product) => {
            const price = product.discount_price || product.price || 0;
            const originalPrice = product.original_price || product.originalPrice || null;
            const discount = product.discount || (originalPrice ? Math.round((1 - price / originalPrice) * 100) : null);
            const image = (product.images && product.images[0]) || product.image || '';
            const rating = product.rating || 4.5;
            const reviews = product.reviews || product.review_count || 0;

            return (
              <div
                key={product.id}
                className="group bg-white border border-gray-200 hover:border-blue-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="relative bg-gray-50 p-4 sm:p-5">
                  <img
                    src={image}
                    alt={product.name}
                    className="w-full h-40 sm:h-48 object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  {discount > 0 && (
                    <Badge className="absolute top-3 left-3 bg-red-500 hover:bg-red-600 text-white text-[10px] sm:text-xs font-semibold">
                      {discount}% OFF
                    </Badge>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-1 mb-2">
                    <Star size={13} className="fill-amber-400 text-amber-400" />
                    <span className="text-xs sm:text-sm text-gray-500">{rating} ({reviews})</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors text-sm sm:text-base leading-snug">
                    {product.name}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-gray-400 mb-3">
                    {product.technology || product.brand || ''}{product.capacity ? ` | ${product.capacity}` : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-xl font-bold text-blue-700">
                      ₹{Number(price).toLocaleString()}
                    </span>
                    {originalPrice && originalPrice > price && (
                      <span className="text-xs sm:text-sm text-gray-400 line-through">
                        ₹{Number(originalPrice).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Bar */}
        <div className="mt-8 sm:mt-10 bg-white rounded-xl p-4 sm:p-6 border border-gray-200 max-w-3xl mx-auto">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg sm:text-2xl font-bold text-blue-700">Free</div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">Installation</div>
            </div>
            <div>
              <div className="text-lg sm:text-2xl font-bold text-blue-700">1 Year</div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">Warranty</div>
            </div>
            <div>
              <div className="text-lg sm:text-2xl font-bold text-blue-700">COD</div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">Available</div>
            </div>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 text-center">
          <Button
            onClick={() => navigate('/products')}
            data-testid="view-all-products-btn"
            className="bg-blue-700 hover:bg-blue-800 text-white px-7 sm:px-8 py-5 rounded-xl font-semibold text-sm sm:text-base group"
          >
            View All RO Systems
            <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Products;
